import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import "react-horizontal-scrolling-menu/dist/styles.css";
import { useContext, ReactElement } from "react";

import { FiArrowLeft, FiArrowRight } from "react-icons/fi";

import { SystemProps, SpaceProps, StyleProps } from "@chakra-ui/styled-system";

import { useTranslator } from "@eo-locale/react";
import omit from "lodash/omit";

import { Box, IconButtonProps } from "@chakra-ui/react";

import { IconButton } from "../button/icon-button";

declare type ItemType = ReactElement<{ itemId: string }>;

/**
 * Note: Important!!
 * Please do not update this package to latest version
 * until node engine version is updated to 18 or above
 */
interface HorizontalScrollMenuProps extends SpaceProps {
  overlapContainer?: boolean;
  spacing?: SystemProps["margin"];
  itemWidth?: SystemProps["width"];
  itemHeight?: SystemProps["height"];
  children: ItemType | ItemType[];
  leftArrowPosition?: number;
  rightArrowPosition?: number;
  overlapContainerProps?: {
    width?: string;
    left?: string;
    right?: string;
  };
}

function HorizontalScrollMenu(props: HorizontalScrollMenuProps) {
  const { translate } = useTranslator();
  const {
    children,
    spacing,
    itemWidth,
    itemHeight,
    overlapContainer = true,
    leftArrowPosition = 2,
    rightArrowPosition = 2,
    overlapContainerProps = {
      width: "100vw",
      left: "calc(-50vw + 50%)"
    }
  } = props;
  const spacingProps = omit(props, [
    "spacing",
    "children",
    "itemWidth",
    "itemHeight",
    "overlapContainer"
  ]);

  function Arrow(props: IconButtonProps) {
    return (
      <IconButton
        isRound
        top="50%"
        zIndex={1}
        position="absolute"
        variant="secondary"
        transform="translateY(-50%)"
        display={{ md: "inline-flex", sm: "none", base: "none" }}
        {...props}
      />
    );
  }

  function RightArrow() {
    const visibility = useContext(VisibilityContext);
    const { isLastItemVisible } = visibility;

    return (
      <Arrow
        right={rightArrowPosition}
        icon={<FiArrowRight />}
        isDisabled={isLastItemVisible}
        aria-label={translate("common.right")}
        display={{
          md: isLastItemVisible ? "none" : "inline-flex",
          sm: "none",
          base: "none"
        }}
        onClick={() =>
          visibility.scrollToItem(
            visibility.getNextElement(),
            "smooth",
            "center"
          )
        }
      />
    );
  }

  function LeftArrow() {
    const visibility = useContext(VisibilityContext);
    const { isFirstItemVisible } = visibility;

    return (
      <Arrow
        left={leftArrowPosition}
        icon={<FiArrowLeft />}
        isDisabled={isFirstItemVisible}
        aria-label={translate("common.left")}
        display={{
          md: isFirstItemVisible ? "none" : "inline-flex",
          sm: "none",
          base: "none"
        }}
        onClick={() =>
          visibility.scrollToItem(
            visibility.getPrevElement(),
            "smooth",
            "center"
          )
        }
      />
    );
  }
  const addOverlapContainerStyles = (
    overlapContainer ? overlapContainerProps : {}
  ) as StyleProps;

  return (
    <Box
      position="relative"
      {...addOverlapContainerStyles}
      sx={{
        "& .react-horizontal-scrolling-menu--scroll-container::-webkit-scrollbar":
          {
            display: "none"
          },
        "& .react-horizontal-scrolling-menu--scroll-container": {
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          ...spacingProps,
          my: "3px"
        },
        "& .react-horizontal-scrolling-menu--item": {
          my: "3px",
          mx: spacing || { md: 3, sm: 2, base: 2 }
        },
        "& .react-horizontal-scrolling-menu--item:first-of-type": {
          ml: 0
        },
        "& .react-horizontal-scrolling-menu--item:last-child": {
          mr: 0
        },
        "& .react-horizontal-scrolling-menu--item > *": {
          width: itemWidth,
          height: itemHeight
        }
      }}
    >
      <ScrollMenu
        Footer={
          <>
            <LeftArrow />
            <RightArrow />
          </>
        }
      >
        {children}
      </ScrollMenu>
    </Box>
  );
}

export { HorizontalScrollMenu };
export type { HorizontalScrollMenuProps };
